// Module: flow-client
// Role: React Query hooks for Flow Engine API endpoints
// Uses: ./client:apiFetch, ./flow-types, @tanstack/react-query
// Used by: miniapp UI components (flow renderer)

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { apiFetch } from './client';
import type {
  GetFlowResponse,
  PresignRequest,
  PresignResponse,
  SubmitCheckpointRequest,
  SubmitCheckpointResponse,
  CheckpointAttempt,
} from './flow-types';

export function hasPendingAttempt(data: GetFlowResponse): boolean {
  const cpId = data.next_checkpoint?.id;
  if (!cpId) return false;
  return data.attempts.some(
    (a: CheckpointAttempt) =>
      a.checkpoint_id === cpId && a.verdict === 'pending',
  );
}

export function useFlow(slotId: string) {
  return useQuery<GetFlowResponse>({
    queryKey: ['flow', slotId],
    queryFn: () =>
      apiFetch<GetFlowResponse>(`/api/v2/buyer/slots/${slotId}/flow`),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return hasPendingAttempt(data) ? 5000 : false;
    },
  });
}

export function useArtefactPresign(slotId: string) {
  return useMutation<PresignResponse, Error, PresignRequest>({
    mutationFn: (body) =>
      apiFetch<PresignResponse>(
        `/api/v2/buyer/slots/${slotId}/artefact/presign`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      ),
  });
}

export function useSubmitCheckpoint(slotId: string, cpId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    SubmitCheckpointResponse,
    Error,
    SubmitCheckpointRequest
  >({
    mutationFn: (body) =>
      apiFetch<SubmitCheckpointResponse>(
        `/api/v2/buyer/slots/${slotId}/checkpoints/${cpId}/submit`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['flow', slotId] });
    },
  });
}

const POLL_TIMEOUT_MS = 60_000;

export function usePollAttempt(attemptId: string, enabled: boolean) {
  const startRef = useRef<number | null>(null);

  return useQuery({
    queryKey: ['attempt', attemptId],
    queryFn: async () => {
      if (startRef.current === null) {
        startRef.current = Date.now();
      }
      return apiFetch<SubmitCheckpointResponse>(
        `/api/v2/buyer/checkpoints/attempts/${attemptId}`,
      );
    },
    enabled,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 2000;
      if (data.verdict !== null && data.verdict !== 'pending') return false;
      if (
        startRef.current !== null &&
        Date.now() - startRef.current >= POLL_TIMEOUT_MS
      ) {
        return false;
      }
      return 2000;
    },
  });
}
